import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Chip,
} from "@heroui/react";
import { useDataTable } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { formatCurrency } from "@/utils";
import { useFetchData } from "@/hooks";
import { FilterData } from "./components";

const initialFilters = {
    account_code: "",
    name: "",
    journal_account_type_id: "",
};

const JournalAccountData = () => {
    const table = useDataTable(
        "journal-account/data",
        null, // No delete endpoint
        {},
        initialFilters,
    );

    const { data: types } = useFetchData("select/journal-account-type");

    const filterLabels = {
        account_code: { label: "No. Akun" },
        name: { label: "Nama Akun" },
        journal_account_type_id: {
            label: "Tipe",
            getValueLabel: (val) => {
                const item = types?.find(
                    (t) => String(t.value) === String(val),
                );
                return item?.title || val;
            },
        },
    };

    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    const skeletonColumns = [
        { name: "No. Akun", skeletonWidth: "w-24" },
        { name: "Nama Akun", skeletonWidth: "w-64" },
        { name: "Tipe", skeletonWidth: "w-32" },
        { name: "Saldo", skeletonWidth: "w-32", align: "end" },
    ];

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data Akun Jurnal."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Akun Jurnal"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Akun Jurnal" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen((prev) => !prev)
                        }
                        activeCount={table.filters.activeCount}
                    />
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={{ types: types || [] }}
            />
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} hideDelete />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Data"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Belum ada Akun Jurnal."
                            }
                        />
                    ) : (
                        <Table aria-label="Journal Account Table" shadow="none">
                            <TableHeader>
                                <TableColumn>No. Akun</TableColumn>
                                <TableColumn>Nama Akun</TableColumn>
                                <TableColumn>Tipe</TableColumn>
                                <TableColumn align="end">Saldo</TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => {
                                    const isParent = !item.parent_id;
                                    return (
                                        <TableRow
                                            key={item.id}
                                            className={
                                                isParent
                                                    ? "bg-default-50/80"
                                                    : ""
                                            }
                                        >
                                            <TableCell>
                                                <ThemedText
                                                    size="sm"
                                                    className={
                                                        isParent
                                                            ? "font-bold"
                                                            : "pl-6"
                                                    }
                                                >
                                                    {item.account_code}
                                                </ThemedText>
                                            </TableCell>
                                            <TableCell>
                                                <ThemedText
                                                    size="sm"
                                                    className={
                                                        isParent
                                                            ? "font-bold"
                                                            : "pl-6"
                                                    }
                                                >
                                                    {item.name}
                                                </ThemedText>
                                            </TableCell>
                                            <TableCell>
                                                {item.type ? (
                                                    <Chip
                                                        size="sm"
                                                        variant="flat"
                                                        color="primary"
                                                    >
                                                        {item.type.name}
                                                    </Chip>
                                                ) : (
                                                    "-"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <ThemedText
                                                    size="sm"
                                                    className={
                                                        isParent
                                                            ? "font-bold"
                                                            : ""
                                                    }
                                                >
                                                    {formatCurrency(
                                                        item.balance,
                                                    )}
                                                </ThemedText>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default JournalAccountData;
